import {ApolloCache, gql, IdGetterObj, OperationVariables, QueryOptions, StoreObject} from '@apollo/client';
import {compact} from '@benchling/js-utils';
import {DocumentNode} from 'graphql';
import _ from 'underscore';

import {assert, assertAndContinue} from 'Asserts';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {RESTTypes} from 'graphql-client/declarative-translations/external/RESTTypes';
import {restArrayToGraphQL} from 'graphql-client/declarative-translations/external/translate';
import FragmentWrapper from 'graphql-client/FragmentWrapper';
import possibleTypeNamesForFragment from 'graphql-client/possibleTypeNamesForFragment';
import QueryWrapper from 'graphql-client/QueryWrapper';
import {MakeFieldsRequired} from 'util/Types';

import {getApolloClient} from './ApolloClient';
import {getFragDefFromDoc} from './AST';
import dataIdFromBenchlingObject from './dataIdFromBenchlingObject';

/**
 * Checks whether `id` is in Apollo's cache via the private EntityStore. Returns `true` when the
 * private API is unavailable so callers degrade to reading every candidate rather than skipping.
 */
function cacheHas(cache: ApolloCache<unknown>, id: string): boolean {
  // @ts-expect-error Accessing private Apollo EntityStore for performance pre-filtering.
  const entityStore = cache.data;
  return typeof entityStore?.has === 'function' ? entityStore.has(id) : true;
}

/**
 * Convenience wrapper to read a fragment from Apollo
 *
 * For unions or interfaces, the fragment may have multiple possible types, and we need to check each
 * possible `ModelTypename:cache_key` combination to find the one (and only one) the data actually exists
 * under.
 *
 * It's easy to forget that readFragment's id param is actually a data ID - computed from __typename and id.
 * This function computes __typename from the given fragment.
 */
export function readFragmentFromCache<T>(wrappedFragment: FragmentWrapper<T>, id?: string): T | null {
  if (isRootFragment(wrappedFragment)) {
    return readRootFragment(wrappedFragment);
  }
  assert(id != null, `Object id required to read non-root fragment ${wrappedFragment.fragmentName}`);
  const {fragment, fragmentName} = wrappedFragment;
  const client = getApolloClient();

  // Handle unions or interfaces - map fragment to possible types, and then map those to cache keys. Then
  // deduplicate those cache keys, because some models (like AnalyticsParameterConfig), use the same cache
  // key for all the possible types.
  //
  // Example 1 (bioentity - different cache keys per type):
  //    BioentityInterface -> Protein, Sequence -> Protein:bio_123, Sequence:bio123
  // Example 2 (parameter config - same cache key across types):
  //    ConfigInterface -> DateConfigNode, StringConfigNode
  //    -> ConfigInterface:apc_123,ConfigInterface:apc_123
  //    -> ConfigInterface:apc_123
  const dataIds = _.unique(
    possibleTypeNamesForFragment(wrappedFragment).map(
      (__typename) => dataIdFromBenchlingObject({__typename, id})!
    )
  ).filter(
    // Pre-filter candidate dataIds to those present in the cache to avoid expensive readFragment
    // misses for union/interface fragments.
    (dataId) => cacheHas(client.cache, dataId)
  );
  const results = compact(dataIds.map((dataId) => client.readFragment({fragment, id: dataId, fragmentName})));
  assertAndContinue(
    results.length <= 1,
    `The Apollo cache returned more than one result when querying fragments. Randomly returning first unordered result.`,
    {results, dataIds},
    {fingerprint: ['Apollo', 'readFragment', 'non-unique-result']}
  );
  return results[0] || null;
}

/**
 * Bulk wrapper around ApolloClient::writeFragment
 *
 * Each write to the client triggers a re-render for all listening components, which can be really painful
 * when writing hundreds of fragments to the cache.
 *
 * This is pointed out in the following issues, but has not been addressed by Apollo yet.
 *
 * If you are trying add a fragment to a collection, be sure to add the fragment to the collection as well.
 * example:
 *
 * @example
 * function addCreatedBenchlingAppToApolloCache(benchlingApp: BenchlingAppGraphQL): void {
 *   if (!cachedBenchlingApps) {
 *     return;
 *   }
 *   writeQuery(
 *     {query: BENCHLING_APP_LISTING_QUERY},
 *     {
 *       benchlingAppListing: {
 *         benchlingApps: [...cachedBenchlingApps, benchlingApp],
 *         __typename: 'BenchlingAppsListingNode',
 *       },
 *     }
 *   );
 * }
 * https://github.com/apollographql/apollo-client/issues/2052
 * https://github.com/apollographql/apollo-feature-requests/issues/7
 */
export function writeFragmentsToCache<T>(fragmentWrapper: FragmentWrapper<T>, data: Array<T>): void {
  if (isRootFragment(fragmentWrapper)) {
    assertAndContinue(
      data.length === 1,
      `Expected only one data object for root fragment ${fragmentWrapper.fragmentName}, received ${data.length}`
    );
    writeRootFragment(fragmentWrapper, data[0]);
  } else {
    writeNonRootFragmentsToCache(fragmentWrapper, data);
  }
}

/**
 * Singular version of writeFragmentsToCache
 */
export function writeFragmentToCache<T>(fragmentWrapper: FragmentWrapper<T>, datum: T): void {
  writeFragmentsToCache(fragmentWrapper, [datum]);
}

function writeNonRootFragmentsToCache<T>(fragmentWrapper: FragmentWrapper<T>, data: Array<T>): void {
  for (const datum of data) {
    getApolloClient().writeFragment({
      fragmentName: fragmentWrapper.fragmentName,
      fragment: fragmentWrapper.fragment,
      data: datum,
    });
  }
}

export function isRootFragment<T>(wrappedFragment: FragmentWrapper<T>): boolean {
  const fragmentDefinition = getFragDefFromDoc(wrappedFragment.fragment, wrappedFragment.fragmentName);
  return fragmentDefinition.typeCondition.name.value === 'Query';
}

/**
 * Dynamically construct a query to read the given fragment from Apollo's cache
 *
 * fragmentName is required if multiple fragments are defined.
 *
 * This is necessary because root fragments don't have IDs - the root Query object doesn't have an ID. This is
 * often used for global collections, e.g. the list of all TagSchemas.
 *
 * Normally dynamic queries are problematic because of our query whitelist, but this query will never go to
 * the server, so it's okay.
 */
function getQueryForRootFragment<T>(wrappedFragment: FragmentWrapper<T>): DocumentNode {
  const {fragment, fragmentName} = wrappedFragment;
  const fragDef = getFragDefFromDoc(fragment, fragmentName);
  return gql`
    query TmpForRootFrag {
      __typename
      ...${fragDef.name.value}
    }
    ${fragment}
  `;
}

/**
 * Convenience wrapper for reading a fragment from the root Query node
 *
 * This is often used when reading a global collection, e.g. the list of all TagSchemas, from Apollo.
 *
 * fragmentName is required if multiple fragments are defined
 */
export function readRootFragment<T>(wrappedFragment: FragmentWrapper<T>): T | null {
  return readFromCache({query: getQueryForRootFragment(wrappedFragment)}, {});
}

export async function doQuerySafe<TQuery, TVariables extends OperationVariables = OperationVariables>(
  wrappedQuery: QueryWrapper<TQuery, TVariables>,
  options: Omit<QueryOptions<TVariables>, 'query'> = {}
): Promise<TQuery> {
  const queryResult = await getApolloClient().query({query: wrappedQuery.query, ...options});
  return queryResult.data;
}

/**
 * A convenience wrapper around the Apollo client's `readQuery`.  This wrapper won't throw exceptions if the
 * query doesn't yet exist.  Instead, it will just return null.
 *
 * @param wrappedQuery - the query to check for in the cache
 * @param options - variables and other options
 */
export function readQuerySafe<TQuery, TVariables = undefined>(
  wrappedQuery: QueryWrapper<TQuery, TVariables>,
  options: Omit<QueryOptions<TVariables>, 'query'> = {}
): TQuery | null {
  return readFromCache(wrappedQuery, options);
}

function readFromCache<TQuery, TVariables = undefined>(
  wrappedQuery: QueryWrapper<TQuery, TVariables>,
  options: Omit<QueryOptions<TVariables>, 'query'>
): TQuery | null {
  /**
   * readQuery() used to throw an error when fields aren't present, which used to be different behavior from
   * readFragment() (returns null).
   *
   * Although this function is somewhat meaningless now that the behavior is the same, this function mostly
   * exists for historical reasons to keep the developer experience the same, and to provide a central
   * location if we ever needed to modify the behavior again in the future (Apollo is known for changing API
   * behavior without much warning).
   */
  return getApolloClient().readQuery({query: wrappedQuery.query, ...options});
}

/**
 * Like `doQuerySafe`, but with `fetchPolicy: 'no-cache'`.
 *
 * Useful for domains where we never use the Apollo cache, like the Notebook's query resolution system.
 * Using `doQueryWithoutCache` makes it safer to add new queries,
 * since there's no way to forget the 'no-cache'.
 */
export async function doQueryWithoutCache<TQuery, TVariables extends OperationVariables>(
  wrappedQuery: QueryWrapper<TQuery, TVariables>,
  options: Omit<QueryOptions<TVariables>, 'query'> = {}
): Promise<TQuery> {
  return await doQuerySafe<TQuery, TVariables>(wrappedQuery, {...options, fetchPolicy: 'no-cache'});
}

/**
 * Convenience wrapper for writing a fragment on the root Query node.
 *
 * fragmentName is required if multiple fragments are defined.
 *
 * This is often used when writing a global collection, e.g. the list of all TagSchemas, to Apollo.
 */
export function writeRootFragment<T>(wrappedFragment: FragmentWrapper<T>, data: T): void {
  getApolloClient().writeQuery({query: getQueryForRootFragment(wrappedFragment), data});
}

export function writeQuery<TQuery, TVariables = undefined>(
  wrappedQuery: QueryWrapper<TQuery, TVariables>,
  data: TQuery,
  variables?: TVariables
): void {
  getApolloClient().writeQuery({query: wrappedQuery.query, variables, data});
}

function isObjectFieldPresentInCache(responseObject: Readonly<StoreObject>, field: string): boolean {
  const {cache} = getApolloClient();
  const dataId = dataIdFromBenchlingObject(responseObject);
  // @ts-expect-error: Reading private cache field `data`
  const cacheObject = cache.data.data[dataId];
  return cacheObject != null && Object.hasOwn(cacheObject, field);
}

/**
 * Read object field from Apollo cache if present, otherwise return null
 *
 * Use in contexts where you don't want to trigger errors or local resolvers if object and its field are
 * absent.
 */
export function readObjectFieldFromCacheIfPresent<
  FieldType extends string,
  ValueType,
  GraphQLType extends {[key in FieldType]: ValueType},
>(
  object: MakeFieldsRequired<IdGetterObj, 'id'>,
  field: FieldType,
  wrappedFragmentToReadObjectField: FragmentWrapper<GraphQLType>
): ValueType | null {
  if (!isObjectFieldPresentInCache(object as unknown as Readonly<StoreObject>, field)) {
    return null;
  }
  const result = readFragmentFromCache(wrappedFragmentToReadObjectField, object.id)!;
  return result[field];
}

export function declarativeWriteRESTObjsToApollo<TDeclaration extends keyof RESTTypes>(
  declarationName: TDeclaration,
  restObjs: Array<RESTTypes[TDeclaration]>
): void {
  writeFragmentsToCache(getFragmentWrapper(declarationName), restArrayToGraphQL(declarationName, restObjs));
}
